import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFactoryStore } from "@/core/hooks/useFactory";
import { useLanguage } from "@/core/LanguageContext";
import { useAuth } from "@/core/AuthContext";
import { useUserFactories } from "@/core/hooks/useUserFactories";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  Activity,
  AlertCircle,
  BarChart3,
  Cpu,
  Factory,
  Gauge,
  GitBranch,
  Layers,
  Loader2,
  RefreshCw,
  Settings,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import { MetricChartDialog } from "@/modules/energy/components/MetricChartDialog";
import { ExposedValueMetricCard } from "@/modules/energy/components/EnergyMetricCard";
import {
  ExposedValueGrid,
  ExposedValueTile,
} from "@/modules/energy/components/EnergyExposedValueGrid";
import { EnergySettingsDialog } from "@/modules/energy/components/EnergySettingsDialog";
import { ModuleStatsPopover } from "@/core/components/exposed-values/ModuleStatsPopover";
import { fetchCurrentValues } from "@/core/api/influxdb";
import { DatapointSelectorDialog, type DatapointSelection } from "@/core/components/DatapointSelectorDialog";
import { toast } from "sonner";
import type {
  Area,
  ExposedValueDefinition,
  ExposedValueMapping,
  FactoryInfo,
  FieldMapping,
  Line,
  LocationScope,
  RealTimeValue,
  Station,
} from "@/modules/energy/types";

const overviewExposedValueNames = [
  "power_total",
  "energy_consumption",
  "power_factor",
  "frequency",
];

const areaHighlightExposedValues = ["power_total", "energy_consumption"];

const energyExposedValueNameGroups = {
  power: ["power_l1", "power_l2", "power_l3", "power_total", "reactive_power", "apparent_power"],
  voltage: [
    "voltage_l1_n",
    "voltage_l2_n",
    "voltage_l3_n",
    "voltage_l1_l2",
    "voltage_l2_l3",
    "voltage_l3_l1",
  ],
  current: ["current_l1", "current_l2", "current_l3", "current_n"],
  energy: ["energy_consumption", "energy_export"],
  quality: ["power_factor", "frequency", "thd_voltage", "thd_current"],
};

const specialExposedValueNames = new Set(Object.values(energyExposedValueNameGroups).flat());

const getLocationKey = (mapping: FieldMapping) => {
  if (mapping.station_id) return `station:${mapping.station_id}`;
  if (mapping.line_id) return `line:${mapping.line_id}`;
  if (mapping.area_id) return `area:${mapping.area_id}`;
  return `factory:${mapping.factory_id}`;
};

const getScopeKey = (scope: LocationScope) => `${scope.type}:${scope.id}`;

const EnergyManagement = () => {
  const { selectedFactoryId } = useFactoryStore();
  const { t } = useLanguage();
  const { profile } = useAuth();
  const { data: userFactories = [] } = useUserFactories();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [realTimeValues, setRealTimeValues] = useState<Record<string, RealTimeValue>>({});
  const [isLoadingValues, setIsLoadingValues] = useState(false);
  const [chartDialogOpen, setChartDialogOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<ExposedValueMapping | null>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorContext, setSelectorContext] = useState<{
    scope: LocationScope;
    exposedValue: ExposedValueDefinition;
  } | null>(null);
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  const [focusedScopeKey, setFocusedScopeKey] = useState<string>("");
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { data: factoryInfo } = useQuery({
    queryKey: ["factory-details", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return null;
      const { data, error } = await supabase
        .schema("core")
        .from("factories")
        .select("id, tenant_id")
        .eq("id", selectedFactoryId)
        .maybeSingle();
      if (error) throw error;
      return data as FactoryInfo | null;
    },
    enabled: !!selectedFactoryId,
  });

  const { data: energyModule } = useQuery({
    queryKey: ["core", "module-registry", "energy"],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema("core")
        .from("module_registry")
        .select("id, name, display_name")
        .eq("name", "energy")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: exposedValues = [], isLoading: loadingExposedValues } = useQuery({
    queryKey: ["core", "exposed-values", energyModule?.id],
    queryFn: async () => {
      if (!energyModule?.id) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("exposed_value_definitions")
        .select("id, name, display_name, description, module_id")
        .eq("module_id", energyModule.id)
        .order("display_name");
      if (error) throw error;
      return data as ExposedValueDefinition[];
    },
    enabled: !!energyModule?.id,
  });

  const {
    data: fieldMappings = [],
    isLoading: loadingMappings,
    refetch: refetchMappings,
  } = useQuery({
    queryKey: ["field-mappings", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("field_mappings")
        .select(
          `
          id,
          factory_id,
          area_id,
          line_id,
          station_id,
          datapoint:datapoints (
            field_name,
            measurement_name,
            display_name,
            bucket_name,
            unit:units (symbol)
          )
        `
        )
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true);
      if (error) throw error;
      const mappings = (data as FieldMapping[]) || [];
      return mappings.filter((mapping) => mapping.datapoint);
    },
    enabled: !!selectedFactoryId,
  });

  const fieldMappingIds = useMemo(() => fieldMappings.map((mapping) => mapping.id), [fieldMappings]);
  const exposedValueIds = useMemo(() => exposedValues.map((value) => value.id), [exposedValues]);

  const {
    data: exposedValueMappings = [],
    refetch: refetchExposedValueMappings,
  } = useQuery({
    queryKey: [
      "core",
      "exposed-value-mappings",
      selectedFactoryId,
      exposedValueIds.join(","),
      fieldMappingIds.join(","),
    ],
    queryFn: async () => {
      if (exposedValueIds.length === 0 || fieldMappingIds.length === 0 || !factoryInfo?.tenant_id) {
        return [];
      }
      const { data, error } = await supabase
        .schema("core")
        .from("datapoint_exposed_value_mapping")
        .select(
          `
          id,
          exposed_value_id,
          field_mapping_id,
          dynamic_key,
          exposed_value:exposed_value_definitions (
            id,
            name,
            display_name,
            description,
            module_id
          ),
          field_mapping:field_mappings (
            id,
            factory_id,
            area_id,
            line_id,
            station_id,
            datapoint:datapoints (
              display_name,
              field_name,
              measurement_name,
              bucket_name,
              unit:units (symbol)
            )
          )
        `
        )
        .eq("tenant_id", factoryInfo.tenant_id)
        .in("exposed_value_id", exposedValueIds)
        .in("field_mapping_id", fieldMappingIds);
      if (error) throw error;
      const mappings = (data as ExposedValueMapping[]) || [];
      return mappings.filter((mapping) => mapping.exposed_value && mapping.field_mapping);
    },
    enabled: exposedValueIds.length > 0 && fieldMappingIds.length > 0 && !!factoryInfo?.tenant_id,
  });

  const { data: areas = [] } = useQuery({
    queryKey: ["factory-areas", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("factory_areas")
        .select("id, name, color")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Area[];
    },
    enabled: !!selectedFactoryId,
  });

  const { data: lines = [] } = useQuery({
    queryKey: ["production-lines", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("production_lines")
        .select("id, area_id, name")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Line[];
    },
    enabled: !!selectedFactoryId,
  });

  const { data: stations = [] } = useQuery({
    queryKey: ["stations", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("stations")
        .select("id, line_id, name")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Station[];
    },
    enabled: !!selectedFactoryId,
  });

  const mappingByLocation = useMemo(() => {
    const map = new Map<string, Map<string, ExposedValueMapping>>();
    exposedValueMappings.forEach((mapping) => {
      if (!mapping.field_mapping) return;
      const locationKey = getLocationKey(mapping.field_mapping);
      const locationMap = map.get(locationKey) ?? new Map<string, ExposedValueMapping>();
      locationMap.set(mapping.exposed_value_id, mapping);
      map.set(locationKey, locationMap);
    });
    return map;
  }, [exposedValueMappings]);

  const getMappingForScope = (scope: LocationScope, exposedValueId: string) => {
    return mappingByLocation.get(getScopeKey(scope))?.get(exposedValueId) ?? null;
  };

  const getFieldMappingsForScope = (scope: LocationScope) => {
    switch (scope.type) {
      case "factory":
        return fieldMappings.filter(
          (mapping) => !mapping.area_id && !mapping.line_id && !mapping.station_id
        );
      case "area":
        return fieldMappings.filter(
          (mapping) => mapping.area_id === scope.id && !mapping.line_id && !mapping.station_id
        );
      case "line":
        return fieldMappings.filter(
          (mapping) => mapping.line_id === scope.id && !mapping.station_id
        );
      case "station":
        return fieldMappings.filter((mapping) => mapping.station_id === scope.id);
      default:
        return [];
    }
  };

  const mappedFieldMappingIds = useMemo(() => {
    return Array.from(new Set(exposedValueMappings.map((mapping) => mapping.field_mapping_id)));
  }, [exposedValueMappings]);

  const fetchRealTimeValues = async () => {
    if (mappedFieldMappingIds.length === 0) return;
    setIsLoadingValues(true);
    try {
      const data = await fetchCurrentValues(mappedFieldMappingIds);
      setRealTimeValues(data);
    } catch (error) {
      console.error("Error fetching real-time values:", error);
    } finally {
      setIsLoadingValues(false);
    }
  };

  useEffect(() => {
    if (mappedFieldMappingIds.length === 0) {
      setRealTimeValues({});
      return;
    }

    fetchRealTimeValues();
    const interval = setInterval(fetchRealTimeValues, 10000);
    return () => clearInterval(interval);
  }, [mappedFieldMappingIds]);

  const handleRefresh = () => {
    refetchMappings();
    refetchExposedValueMappings();
    fetchRealTimeValues();
  };

  const totalMappings = exposedValueMappings.length;
  const areaCount = new Set(
    Array.from(mappingByLocation.keys()).filter((key) => key.startsWith("area:"))
  ).size;
  const lineCount = new Set(
    Array.from(mappingByLocation.keys()).filter((key) => key.startsWith("line:"))
  ).size;
  const stationCount = new Set(
    Array.from(mappingByLocation.keys()).filter((key) => key.startsWith("station:"))
  ).size;

  const overviewDefinitions = useMemo(() => {
    return overviewExposedValueNames
      .map((name) => exposedValues.find((definition) => definition.name === name))
      .filter(Boolean) as ExposedValueDefinition[];
  }, [exposedValues]);

  const highlightDefinitions = useMemo(() => {
    return areaHighlightExposedValues
      .map((name) => exposedValues.find((definition) => definition.name === name))
      .filter(Boolean) as ExposedValueDefinition[];
  }, [exposedValues]);

  const exposedValueGroups = useMemo(() => {
    const groupConfigs = [
      {
        id: "power",
        title: t("mod_energy.energy_management.group.power.title", "Power"),
        description: t(
          "mod_energy.energy_management.group.power.description",
          "Active, reactive, and apparent power across phases."
        ),
        icon: Zap,
        names: energyExposedValueNameGroups.power,
      },
      {
        id: "voltage",
        title: t("mod_energy.energy_management.group.voltage.title", "Voltage"),
        description: t(
          "mod_energy.energy_management.group.voltage.description",
          "Phase-to-neutral and line-to-line voltage readings."
        ),
        icon: Gauge,
        names: energyExposedValueNameGroups.voltage,
      },
      {
        id: "current",
        title: t("mod_energy.energy_management.group.current.title", "Current"),
        description: t(
          "mod_energy.energy_management.group.current.description",
          "Phase and neutral current draw signals."
        ),
        icon: Activity,
        names: energyExposedValueNameGroups.current,
      },
      {
        id: "energy",
        title: t("mod_energy.energy_management.group.energy.title", "Energy"),
        description: t(
          "mod_energy.energy_management.group.energy.description",
          "Consumption and export totals for the selected level."
        ),
        icon: BarChart3,
        names: energyExposedValueNameGroups.energy,
      },
      {
        id: "quality",
        title: t("mod_energy.energy_management.group.quality.title", "Quality"),
        description: t(
          "mod_energy.energy_management.group.quality.description",
          "Power factor, frequency, and harmonic distortion signals."
        ),
        icon: AlertCircle,
        names: energyExposedValueNameGroups.quality,
      },
    ];

    const mappedGroups = groupConfigs.map((group) => ({
      id: group.id,
      title: group.title,
      description: group.description,
      icon: group.icon,
      definitions: group.names
        .map((name) => exposedValues.find((definition) => definition.name === name))
        .filter(Boolean) as ExposedValueDefinition[],
    }));

    const otherDefinitions = exposedValues.filter(
      (definition) => !specialExposedValueNames.has(definition.name)
    );

    const combinedGroups = otherDefinitions.length
      ? [
          ...mappedGroups,
          {
            id: "other",
            title: t("mod_energy.energy_management.group.other.title", "Additional Signals"),
            description: t(
              "mod_energy.energy_management.group.other.description",
              "Other exposed values registered for this module."
            ),
            icon: Layers,
            definitions: otherDefinitions,
          },
        ]
      : mappedGroups;

    return combinedGroups.filter((group) => group.definitions.length > 0);
  }, [exposedValues, t]);

  const scopedSelectorMappings = useMemo(() => {
    if (!selectorContext) return [];
    return getFieldMappingsForScope(selectorContext.scope);
  }, [selectorContext, fieldMappings]);

  const scopeOptions = useMemo(() => {
    if (!selectedFactoryId) return [];
    const options: LocationScope[] = [
      {
        type: "factory",
        id: selectedFactoryId,
        label: t("mod_energy.energy_management.level.factory", "Factory"),
      },
      ...areas.map((area) => ({
        type: "area" as const,
        id: area.id,
        label: `${t("mod_energy.energy_management.level.area", "Area")} • ${area.name}`,
      })),
      ...lines.map((line) => ({
        type: "line" as const,
        id: line.id,
        label: `${t("mod_energy.energy_management.level.line", "Line")} • ${line.name}`,
      })),
      ...stations.map((station) => ({
        type: "station" as const,
        id: station.id,
        label: `${t("mod_energy.energy_management.level.station", "Station")} • ${station.name}`,
      })),
    ];
    return options;
  }, [areas, lines, selectedFactoryId, stations, t]);

  useEffect(() => {
    if (!selectedFactoryId) return;
    setFocusedScopeKey((current) => current || `factory:${selectedFactoryId}`);
  }, [selectedFactoryId]);

  useEffect(() => {
    if (scopeOptions.length === 0) return;
    const exists = scopeOptions.some((scope) => getScopeKey(scope) === focusedScopeKey);
    if (!exists) {
      setFocusedScopeKey(getScopeKey(scopeOptions[0]));
    }
  }, [scopeOptions, focusedScopeKey]);

  const focusedScope = useMemo(() => {
    if (scopeOptions.length === 0) return null;
    return scopeOptions.find((scope) => getScopeKey(scope) === focusedScopeKey) ?? scopeOptions[0];
  }, [focusedScopeKey, scopeOptions]);

  const scopeOptionsByType = useMemo(() => {
    return {
      factory: scopeOptions.filter((scope) => scope.type === "factory"),
      areas: scopeOptions.filter((scope) => scope.type === "area"),
      lines: scopeOptions.filter((scope) => scope.type === "line"),
      stations: scopeOptions.filter((scope) => scope.type === "station"),
    };
  }, [scopeOptions]);

  const hasDatapoints = fieldMappings.length > 0;

  const canEditMappings = useMemo(() => {
    if (profile?.is_super_admin) return true;
    const roleName = userFactories
      .find((role) => role.factory_id === selectedFactoryId)?.role
      ?.toLowerCase();
    if (!roleName) return false;
    return roleName.includes("supervisor") || roleName.includes("manager") || roleName.includes("admin");
  }, [profile?.is_super_admin, selectedFactoryId, userFactories]);

  const isMappingDisabled = !hasDatapoints || !canEditMappings;

  const selectedMappedId = selectorContext
    ? getMappingForScope(selectorContext.scope, selectorContext.exposedValue.id)?.field_mapping_id ?? null
    : null;

  const handleSelectDatapoint = async (mapping: DatapointSelection) => {
    if (!selectorContext || !factoryInfo) return;
    if (!canEditMappings) {
      toast.error(
        t("mod_energy.energy_management.toast.permission", "You do not have access to edit mappings")
      );
      return;
    }
    setIsSavingMapping(true);
    try {
      const existingMapping = getMappingForScope(
        selectorContext.scope,
        selectorContext.exposedValue.id
      );
      if (existingMapping) {
        const { error: deleteError } = await supabase
          .schema("core")
          .from("datapoint_exposed_value_mapping")
          .delete()
          .eq("id", existingMapping.id);
        if (deleteError) throw deleteError;
      }

      const { error } = await supabase
        .schema("core")
        .from("datapoint_exposed_value_mapping")
        .insert({
          tenant_id: factoryInfo.tenant_id,
          field_mapping_id: mapping.id,
          exposed_value_id: selectorContext.exposedValue.id,
        });
      if (error) throw error;

      toast.success(
        t("mod_energy.energy_management.toast.mapped", "Exposed value mapped")
      );
      queryClient.invalidateQueries({
        queryKey: ["core", "exposed-value-mappings", selectedFactoryId],
      });
      setSelectorOpen(false);
    } catch (error) {
      toast.error(
        t("mod_energy.energy_management.toast.failed", "Failed to map exposed value")
      );
      console.error(error);
    } finally {
      setIsSavingMapping(false);
    }
  };

  if (!selectedFactoryId) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Card className="p-8 text-center">
          <Factory className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">
            {t("mod_energy.energy_management.empty.factory.title", "No Factory Selected")}
          </h2>
          <p className="text-muted-foreground">
            {t(
              "mod_energy.energy_management.empty.factory.description",
              "Please select a factory to view energy data."
            )}
          </p>
        </Card>
      </div>
    );
  }

  const factoryScope: LocationScope = {
    type: "factory",
    id: selectedFactoryId,
    label: t("mod_energy.energy_management.level.factory", "Factory"),
  };

  const isLoading = loadingMappings || loadingExposedValues;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Zap className="h-8 w-8 text-warning" />
            {t("mod_energy.energy_management.title", "Energy Management")}
            <ModuleStatsPopover
              totalMappings={totalMappings}
              areaCount={areaCount}
              lineCount={lineCount}
              stationCount={stationCount}
              isLoading={isLoading}
              iconColorClass="text-warning"
            />
          </h1>
          <p className="text-muted-foreground">
            {t(
              "mod_energy.energy_management.subtitle",
              "Monitor energy KPIs and drill into production areas."
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isLoadingValues && (
            <Badge variant="secondary" className="gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t("mod_energy.energy_management.updating", "Updating...")}
            </Badge>
          )}
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingValues ? "animate-spin" : ""}`} />
            {t("mod_energy.energy_management.refresh", "Refresh")}
          </Button>
          <Button variant="outline" onClick={() => setSettingsOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            {t("mod_energy.energy_management.settings", "Settings")}
          </Button>
          <Link to="/admin/uns-mapper">
            <Button variant="outline">
              <GitBranch className="h-4 w-4 mr-2" />
              {t("mod_energy.energy_management.unsMapper", "UNS Mapper")}
            </Button>
          </Link>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <Gauge className="h-4 w-4" />
            {t("mod_energy.energy_management.tabs.overview", "Overview")}
          </TabsTrigger>
          <TabsTrigger value="areas" className="gap-2">
            <Layers className="h-4 w-4" />
            {t("mod_energy.energy_management.tabs.areas", "By Area")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : exposedValues.length === 0 ? (
            <Card className="p-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h2 className="text-xl font-semibold mb-2">
                {t("mod_energy.energy_management.empty.exposed.title", "No exposed values found")}
              </h2>
              <p className="text-muted-foreground">
                {t(
                  "mod_energy.energy_management.empty.exposed.description",
                  "Ensure the energy module exposed values are registered."
                )}
              </p>
            </Card>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-warning" />
                    {t("mod_energy.energy_management.kpis.title", "Factory KPIs")}
                  </CardTitle>
                  <CardDescription>
                    {t(
                      "mod_energy.energy_management.kpis.description",
                      "Snapshot of key energy readings."
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {factoryScope && overviewDefinitions.map((definition) => (
                      <ExposedValueTile
                        key={definition.id}
                        definition={definition}
                        mapping={getMappingForScope(factoryScope, definition.id)}
                        realTimeValues={realTimeValues}
                        onSelect={() => {
                          setSelectorContext({ scope: factoryScope, exposedValue: definition });
                          setSelectorOpen(true);
                        }}
                        onOpenChart={(mapping) => {
                          setSelectedMapping(mapping);
                          setChartDialogOpen(true);
                        }}
                              disabled={isMappingDisabled}

                      />
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Factory className="h-5 w-5 text-primary" />
                    {t("mod_energy.energy_management.areaHighlights.title", "Area Highlights")}
                  </CardTitle>
                  <CardDescription>
                    {t(
                      "mod_energy.energy_management.areaHighlights.description",
                      "Quick view of power and consumption by area."
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {areas.map((area) => {
                      const scope: LocationScope = { type: "area", id: area.id, label: area.name };
                      return (
                        <Card key={area.id} className="border-border/60">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: area.color }}
                              />
                              {area.name}
                            </CardTitle>
                            <CardDescription>
                              {highlightDefinitions.length} {t("mod_energy.energy_management.areaHighlights.tracked", "tracked KPIs")}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-1 gap-3">
                              {highlightDefinitions.map((definition) => (
                                <ExposedValueTile
                                  key={definition.id}
                                  definition={definition}
                                  mapping={getMappingForScope(scope, definition.id)}
                                  realTimeValues={realTimeValues}
                                  compact
                                  onSelect={() => {
                                    setSelectorContext({ scope, exposedValue: definition });
                                    setSelectorOpen(true);
                                  }}
                                  onOpenChart={(mapping) => {
                                    setSelectedMapping(mapping);
                                    setChartDialogOpen(true);
                                  }}
                        disabled={isMappingDisabled}

                                />
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                      {areas.length === 0 && (
                      <div className="col-span-full flex flex-col items-center justify-center py-12 text-muted-foreground">
                        <Factory className="h-12 w-12 opacity-30 mb-3" />
                        <p className="text-sm">
                          {t("mod_energy.energy_management.areaHighlights.empty", "No areas configured")}
                        </p>
                        <Link
                          to="/admin/factory-structure"
                          className="text-primary text-sm hover:underline mt-1"
                        >
                          {t(
                            "mod_energy.energy_management.areaHighlights.cta",
                            "Set up factory structure →"
                          )}
                        </Link>
                      </div>
                    )}

                  </div>
                </CardContent>
              </Card>

              {focusedScope && (
                <Card>
                  <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="space-y-1">
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="h-5 w-5 text-primary" />
                        {t("mod_energy.energy_management.levelOverview.title", "Energy Signals")}
                      </CardTitle>
                      <CardDescription>
                        {t(
                          "mod_energy.energy_management.levelOverview.description",
                          "Select a level to explore every exposed value with live KPIs and trends."
                        )}
                      </CardDescription>
                    </div>
                    <div className="w-full md:w-[280px]">
                      <Select value={focusedScopeKey} onValueChange={setFocusedScopeKey}>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t(
                              "mod_energy.energy_management.levelOverview.selectPlaceholder",
                              "Choose level"
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {scopeOptionsByType.factory.map((scope) => (
                            <SelectItem key={scope.id} value={getScopeKey(scope)}>
                              {scope.label}
                            </SelectItem>
                          ))}
                          {scopeOptionsByType.areas.length > 0 && <SelectSeparator />}
                          {scopeOptionsByType.areas.map((scope) => (
                            <SelectItem key={scope.id} value={getScopeKey(scope)}>
                              {scope.label}
                            </SelectItem>
                          ))}
                          {scopeOptionsByType.lines.length > 0 && <SelectSeparator />}
                          {scopeOptionsByType.lines.map((scope) => (
                            <SelectItem key={scope.id} value={getScopeKey(scope)}>
                              {scope.label}
                            </SelectItem>
                          ))}
                          {scopeOptionsByType.stations.length > 0 && <SelectSeparator />}
                          {scopeOptionsByType.stations.map((scope) => (
                            <SelectItem key={scope.id} value={getScopeKey(scope)}>
                              {scope.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {exposedValueGroups.map((group) => (
                      <div key={group.id} className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 rounded-lg bg-muted/60 p-2">
                            <group.icon className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-semibold">{group.title}</p>
                            <p className="text-sm text-muted-foreground">{group.description}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                          {group.definitions.map((definition) => (
                            <ExposedValueMetricCard
                              key={definition.id}
                              definition={definition}
                              mapping={getMappingForScope(focusedScope, definition.id)}
                              realTimeValues={realTimeValues}
                              onSelect={() => {
                                setSelectorContext({ scope: focusedScope, exposedValue: definition });
                                setSelectorOpen(true);
                              }}
                              onOpenChart={(mapping) => {
                                setSelectedMapping(mapping);
                                setChartDialogOpen(true);
                              }}
                                  disabled={isMappingDisabled}

                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="areas" className="mt-6">
          <ScrollArea className="h-[700px]">
            <div className="space-y-6">
              {areas.length === 0 ? (
                <Card className="p-8 text-center">
                  <Layers className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h2 className="text-xl font-semibold mb-2">
                    {t("mod_energy.energy_management.areas.empty.title", "No Areas Configured")}
                  </h2>
                  <p className="text-muted-foreground mb-4">
                    {t(
                      "mod_energy.energy_management.areas.empty.description",
                      "Set up your factory structure to organize energy metrics by area."
                    )}
                  </p>
                  <Link to="/admin/factory-structure">
                    <Button>
                      {t(
                        "mod_energy.energy_management.areas.empty.cta",
                        "Configure Factory Structure"
                      )}
                    </Button>
                  </Link>
                </Card>
              ) : (
                areas.map((area) => {
                  const areaScope: LocationScope = { type: "area", id: area.id, label: area.name };
                  const areaLines = lines.filter((line) => line.area_id === area.id);
                  return (
                    <Card key={area.id}>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: area.color }}
                          />
                          {area.name}
                          <Badge variant="secondary">
                            {areaLines.length} {t("mod_energy.energy_management.areas.lines", "lines")}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-3">
                            {t("mod_energy.energy_management.areas.values", "Area Values")}
                          </p>
                          <ExposedValueGrid
                            definitions={exposedValues}
                            scope={areaScope}
                            getMapping={getMappingForScope}
                            realTimeValues={realTimeValues}
                            onSelect={(definition) => {
                              setSelectorContext({ scope: areaScope, exposedValue: definition });
                              setSelectorOpen(true);
                            }}
                            onOpenChart={(mapping) => {
                              setSelectedMapping(mapping);
                              setChartDialogOpen(true);
                            }}
                                      isSelectionDisabled={isMappingDisabled}

                          />
                        </div>

                        {areaLines.map((line) => {
                          const lineScope: LocationScope = {
                            type: "line",
                            id: line.id,
                            label: line.name,
                          };
                          const lineStations = stations.filter((station) => station.line_id === line.id);

                          return (
                            <div key={line.id} className="border rounded-lg p-4 space-y-4">
                              <div className="flex items-center gap-2">
                                <GitBranch className="h-4 w-4 text-chart-2" />
                                <span className="font-medium">{line.name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {lineStations.length} {t("mod_energy.energy_management.areas.stations", "stations")}
                                </Badge>
                              </div>

                              <ExposedValueGrid
                                definitions={exposedValues}
                                scope={lineScope}
                                getMapping={getMappingForScope}
                                realTimeValues={realTimeValues}
                                onSelect={(definition) => {
                                  setSelectorContext({ scope: lineScope, exposedValue: definition });
                                  setSelectorOpen(true);
                                }}
                                onOpenChart={(mapping) => {
                                  setSelectedMapping(mapping);
                                  setChartDialogOpen(true);
                                }}
                                isSelectionDisabled={isMappingDisabled}
                              />

                              {lineStations.map((station) => {
                                const stationScope: LocationScope = {
                                  type: "station",
                                  id: station.id,
                                  label: station.name,
                                };

                                return (
                                  <div
                                    key={station.id}
                                    className="border rounded-lg p-4 ml-4 space-y-3"
                                  >
                                    <div className="flex items-center gap-2">
                                      <Cpu className="h-4 w-4 text-success" />
                                      <span className="text-sm font-medium">{station.name}</span>
                                    </div>
                                    <ExposedValueGrid
                                      definitions={exposedValues}
                                      scope={stationScope}
                                      getMapping={getMappingForScope}
                                      realTimeValues={realTimeValues}
                                      onSelect={(definition) => {
                                        setSelectorContext({ scope: stationScope, exposedValue: definition });
                                        setSelectorOpen(true);
                                      }}
                                      onOpenChart={(mapping) => {
                                        setSelectedMapping(mapping);
                                        setChartDialogOpen(true);
                                      }}
                            isSelectionDisabled={isMappingDisabled}

                                    />
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </TabsContent>

      </Tabs>

      {selectedMapping && (
        <MetricChartDialog
          open={chartDialogOpen}
          onOpenChange={setChartDialogOpen}
          mappingId={selectedMapping.field_mapping_id}
          displayName={
            selectedMapping.exposed_value?.display_name ||
            selectedMapping.field_mapping?.datapoint?.display_name ||
            t("mod_energy.energy_management.metric.unknown", "Unknown")
          }
          unit={selectedMapping.field_mapping?.datapoint?.unit?.symbol || null}
          currentValue={realTimeValues[selectedMapping.field_mapping_id]?.value}
        />
      )}

      <EnergySettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        exposedValues={exposedValues}
        factoryScope={factoryScope}
        areas={areas}
        lines={lines}
        stations={stations}
        hasDatapoints={hasDatapoints && canEditMappings}
        realTimeValues={realTimeValues}
        getMappingForScope={getMappingForScope}
        onSelectMapping={(scope, definition) => {
          setSelectorContext({ scope, exposedValue: definition });
          setSelectorOpen(true);
        }}
        onOpenChart={(mapping) => {
          setSelectedMapping(mapping);
          setChartDialogOpen(true);
        }}
      />

      <DatapointSelectorDialog
        open={selectorOpen}
        onOpenChange={(open) => {
          setSelectorOpen(open);
          if (!open) setSelectorContext(null);
        }}
        scopedMappings={scopedSelectorMappings}
        allMappings={fieldMappings}
        selectedId={selectorContext
          ? getMappingForScope(selectorContext.scope, selectorContext.exposedValue.id)?.field_mapping_id ?? null
          : null}
        onSelect={handleSelectDatapoint}
        title={
          selectorContext
            ? t(
                "mod_energy.energy_management.selector.title",
                `Select datapoint for ${selectorContext.exposedValue.display_name}`
              )
            : t("mod_energy.energy_management.selector.titleFallback", "Select datapoint")
        }
        description={
          selectorContext
            ? t(
                "mod_energy.energy_management.selector.description",
                `Assign a datapoint at ${selectorContext.scope.label} level.`
              )
            : t(
                "mod_energy.energy_management.selector.descriptionFallback",
                "Choose an existing datapoint mapping."
              )
        }
      />


      {isSavingMapping && (
        <div className="fixed bottom-6 right-6">
          <Badge variant="secondary" className="gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t("mod_energy.energy_management.saving", "Saving mapping...")}
          </Badge>
        </div>
      )}
    </div>
  );
};

export default EnergyManagement;
